import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap, forkJoin } from 'rxjs';
import { UserPreferencesService, UserPreferences } from 'app/core/user/user-preferences.service';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { environment } from 'environments/environment';

@Injectable({
    providedIn: 'root',
})
export class NavigationService {
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    /**
     * Constructor
     */
    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userPreferencesService: UserPreferencesService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    private _rawNavigation: Navigation | null = null;

    /**
     * Get all navigation data
     */
    get(): Observable<any> {
        return forkJoin([
            this._httpClient.get<Navigation>(environment.urlBase + 'common/navigation'),
            this._userPreferencesService.getPreferences()
        ]).pipe(
            tap(([navigation, preferences]) => {
                this._rawNavigation = navigation;
                // Process navigation to fill user preferences
                const updatedNavigation = this._fillUserPreferences(navigation, preferences);
                this._navigation.next(updatedNavigation);

                // Listen for future preference changes
                this._userPreferencesService.preferences$.subscribe(prefs => {
                    if (this._rawNavigation) {
                        const updated = this._fillUserPreferences(this._rawNavigation, prefs);
                        this._navigation.next(updated);
                    }
                });
            }),
        );
    }

    private _fillUserPreferences(navigation: Navigation, preferences: UserPreferences): Navigation {
        if (!preferences) return navigation;

        // Clone navigation to avoid mutation issues
        const nav = JSON.parse(JSON.stringify(navigation));

        // Find all flat items to lookup by ID
        const flatItems: FuseNavigationItem[] = [];
        const traverse = (items: FuseNavigationItem[]) => {
            items.forEach(item => {
                flatItems.push(item);
                if (item.children) traverse(item.children);
            });
        };
        // Traverse 'default' or 'compact', etc. usually 'default' is the main one used for structure.
        // Assuming 'data' or similar is returned from API. 
        // Based on previous data.ts, it returns an array of items directly usually, 
        // OR the API returns { default: [], compact: [], ... } 
        // Navigation types interface check needed? 
        // The mock api returns { default: [...], compact: [...], ... } usually.

        // Taking a guess at structure based on Fuse default.
        // If navigation is the object with keys 'default', 'compact', etc.
        const allItems: FuseNavigationItem[] = [];
        Object.values(nav).forEach((navGroup: any) => {
            if (Array.isArray(navGroup)) {
                traverse(navGroup);
            }
        });

        const favoritesGroup: FuseNavigationItem = {
            id: 'favorites',
            title: 'Favoritos',
            type: 'group',
            icon: 'heroicons_outline:star',
            children: []
        };

        const recentsGroup: FuseNavigationItem = {
            id: 'recents',
            title: 'Recientes',
            type: 'group',
            icon: 'heroicons_outline:clock',
            children: []
        };

        // Populate Favorites
        if (preferences.favoritos) {
            preferences.favoritos.forEach(favId => {
                // Find item by ID. 
                // Note: We might need to look in the ORIGINAL constant if the API returns just structure.
                // But here we rely on what we fetched.
                const item = flatItems.find(i => i.id === favId || i.link === favId); // support link as ID too
                if (item) {
                    // Clone item and change type to basic link if needed
                    favoritesGroup.children.push({
                        ...item,
                        id: 'fav-' + item.id, // unique ID
                        type: 'basic'
                    });
                }
            });
        }

        // Populate Recents
        if (preferences.recientes) {
            preferences.recientes.forEach(recentId => {
                const item = flatItems.find(i => i.id === recentId || i.link === recentId);
                if (item) {
                    recentsGroup.children.push({
                        ...item,
                        id: 'recent-' + item.id,
                        type: 'basic'
                    });
                }
            });
        }

        // Add to the beginning of 'default' navigation (sidebar)
        if (nav.default) {
            // Add separator or just prepend
            if (recentsGroup.children.length > 0) nav.default.unshift(recentsGroup);
            if (favoritesGroup.children.length > 0) nav.default.unshift(favoritesGroup);
        }

        // Also handle 'futuristic' or others if used, but let's stick to default for now.

        return nav;
    }
}
